import { INBOX_EVENT, InboxEventSchema, inboxTopics } from '@fundline/shared/messages/realtime';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { queryKeys } from './queryKeys.ts';
import {
  type ConnectionClock,
  createBatcher,
  INVALIDATE_DEBOUNCE_MS,
  isReconnecting,
  shouldPoll,
} from './realtime-health.ts';
import { supabase } from './supabase.ts';

/**
 * Live inbox (PLAN.md §8). Joins the member's private Realtime topics (their own; admins also the
 * workspace's admins topic). A notification carries ids only: it invalidates queries, which refetch
 * through RLS (CLAUDE.md: never patch caches with payload data).
 *
 * Phones suspend tabs and drop sockets, so on visibilitychange/focus/online, and whenever the channels
 * join again, everything open is refetched (catch-up). If the channels stay down for 10 s, the shell
 * shows "Reconnecting…" and the inbox is refetched every 10 s until they are back.
 */
export function useInboxRealtime(
  workspaceId: string | null,
  userId: string | null,
  isAdmin: boolean,
): { reconnecting: boolean } {
  const queryClient = useQueryClient();
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => {
    if (!workspaceId || !userId) return;
    const client = supabase();
    const topics = inboxTopics(workspaceId, userId, isAdmin);
    const joined = new Map(topics.map((t) => [t, false]));
    const clock: ConnectionClock = { downSince: Date.now(), lastPollAt: Date.now() };
    let everJoined = false;
    let channels: RealtimeChannel[] = [];
    let stopped = false;

    const catchUp = () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inbox(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.merchants(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications(workspaceId) });
    };
    const batcher = createBatcher(INVALIDATE_DEBOUNCE_MS, (merchantIds) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inbox(workspaceId) });
      for (const id of merchantIds) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.merchant(workspaceId, id) });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.merchantTimeline(workspaceId, id),
        });
      }
    });

    const allJoined = () => [...joined.values()].every(Boolean);
    const onStatus = (topic: string, status: string) => {
      joined.set(topic, status === 'SUBSCRIBED');
      if (allJoined()) {
        if (clock.downSince !== null) {
          clock.downSince = null;
          setReconnecting(false);
          // Back after a gap: anything broadcast meanwhile was missed, so refetch what is open.
          if (everJoined) catchUp();
          everJoined = true;
        }
      } else if (clock.downSince === null) {
        clock.downSince = Date.now();
      }
    };

    const connect = async () => {
      // Private channels authorize with the user's access token (realtime.messages policy).
      await client.realtime.setAuth();
      if (stopped) return;
      channels = topics.map((topic) =>
        client
          .channel(topic, { config: { private: true } })
          .on('broadcast', { event: INBOX_EVENT }, (message) => {
            const parsed = InboxEventSchema.safeParse(message.payload);
            if (!parsed.success) return;
            if (parsed.data.t === 'notif') {
              void queryClient.invalidateQueries({
                queryKey: queryKeys.notifications(workspaceId),
              });
            } else {
              batcher.add(parsed.data.merchant_id);
            }
          })
          .subscribe((status) => onStatus(topic, status)),
      );
    };
    const reconnect = () => {
      for (const channel of channels) void client.removeChannel(channel);
      channels = [];
      for (const topic of topics) joined.set(topic, false);
      if (clock.downSince === null) clock.downSince = Date.now();
      void connect();
    };

    const onWake = () => {
      if (document.visibilityState === 'hidden') return;
      catchUp();
      if (!allJoined()) reconnect();
    };

    const tick = setInterval(() => {
      const now = Date.now();
      setReconnecting(isReconnecting(clock, now));
      if (shouldPoll(clock, now)) {
        clock.lastPollAt = now;
        catchUp();
      }
    }, 2_000);

    void connect();
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);
    return () => {
      stopped = true;
      clearInterval(tick);
      batcher.cancel();
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
      for (const channel of channels) void client.removeChannel(channel);
    };
  }, [workspaceId, userId, isAdmin, queryClient]);

  return { reconnecting };
}
